import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { useMutation, useQuery } from "convex/react";
import { useMsal } from "@azure/msal-react";
import { api } from "../../../convex/_generated/api";
import {
  TERMS_VERSION,
  TERMS_EFFECTIVE,
  TERMS_TITLE,
  TERMS_POINTS,
  TERMS_ACCEPT_LABEL,
} from "../legal/terms";

/**
 * First-login clickwrap gate. Blocks the app until the signed-in user accepts
 * the current AI Use Terms (human-review acknowledgment). Acceptance is
 * recorded per (email, TERMS_VERSION) in Convex + the audit trail; bumping
 * TERMS_VERSION re-prompts everyone.
 *
 * Fail-safe: while the acceptance status is loading we render children-blocked
 * with a spinner (never the app), and if the email can't be resolved we still
 * show the gate.
 */
export function TermsGate({ children }: { children: React.ReactNode }) {
  const { instance, accounts } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const account = activeAccount ?? accounts[0];
  const claims = (account?.idTokenClaims ?? {}) as Record<string, unknown>;
  const claimStr = (k: string) =>
    typeof claims[k] === "string" ? (claims[k] as string).trim() : "";
  const email =
    (account?.username && account.username.trim()) ||
    claimStr("preferred_username") ||
    claimStr("upn") ||
    claimStr("email") ||
    "";
  const displayName =
    (typeof claims["name"] === "string" ? (claims["name"] as string) : "") ||
    account?.name ||
    undefined;

  const status = useQuery(
    api.termsAcceptance.getStatus,
    email ? { email, version: TERMS_VERSION } : "skip",
  );
  const acceptMutation = useMutation(api.termsAcceptance.accept);

  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const handleAccept = useCallback(async () => {
    if (!checked || !email) return;
    setSubmitting(true);
    setError(undefined);
    try {
      await acceptMutation({
        email,
        version: TERMS_VERSION,
        name: displayName,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      });
      // Convex reactivity will flip `status.accepted` → true and unmount the gate.
    } catch {
      setSubmitting(false);
      setError("Could not record your acceptance. Please try again.");
    }
  }, [checked, email, displayName, acceptMutation]);

  const accepted = status?.accepted === true;

  const gateOpen = useMemo(() => {
    // Show the gate once we know the user has NOT accepted. While status is
    // still loading (undefined), block with a spinner rather than flashing the
    // app or the dialog.
    return status !== undefined && !accepted;
  }, [status, accepted]);

  if (accepted) return <>{children}</>;

  // Not yet accepted (or still loading): never render the app behind the gate.
  return (
    <>
      <Dialog
        open
        maxWidth="sm"
        fullWidth
        // Modal cannot be dismissed by backdrop/escape — acceptance is required.
        disableEscapeKeyDown
        aria-labelledby="terms-gate-title"
      >
        <DialogTitle id="terms-gate-title" sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <VerifiedUserOutlinedIcon sx={{ color: "var(--c-link-accent)" }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                {TERMS_TITLE}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Version {TERMS_VERSION} · effective {TERMS_EFFECTIVE}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {status === undefined ? (
            <Stack alignItems="center" sx={{ py: 4 }} spacing={2}>
              <CircularProgress size={22} />
              <Typography variant="body2" color="text.secondary">
                Loading terms…
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Before you use Squad Finance, please read and acknowledge the following. This
                system is AI-assisted and requires your review.
              </Typography>
              {TERMS_POINTS.map((p, i) => (
                <Box key={i}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "var(--c-text-primary)" }}>
                    {p.heading}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--c-text-secondary)", mt: 0.25 }}>
                    {p.body}
                  </Typography>
                  {i < TERMS_POINTS.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ flexDirection: "column", alignItems: "stretch", gap: 1, px: 3, py: 2 }}>
          <FormControlLabel
            sx={{ alignItems: "flex-start", m: 0 }}
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={status === undefined || submitting}
                sx={{ pt: 0 }}
              />
            }
            label={
              <Typography variant="body2" sx={{ color: "var(--c-text-primary)" }}>
                {TERMS_ACCEPT_LABEL}
              </Typography>
            }
          />
          {error && (
            <Typography variant="caption" sx={{ color: "var(--c-metric-error, #ff7675)" }}>
              {error}
            </Typography>
          )}
          {!email && status !== undefined && (
            <Typography variant="caption" sx={{ color: "var(--c-metric-warning, #fdcb6e)" }}>
              We couldn't read your account email — please re-sign in.
            </Typography>
          )}
          <Button
            variant="contained"
            size="large"
            disabled={!checked || !email || submitting || status === undefined}
            onClick={handleAccept}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? "Recording…" : "Accept & Continue"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* App intentionally NOT rendered behind the gate until accepted. */}
      {!gateOpen && null}
    </>
  );
}

export default TermsGate;
